import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconChevronLeft,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconFileInvoice,
  IconContract,
  IconCalendar,
  IconCurrencyDollar,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useCommitment,
  useUpdateCommitment,
  useDeleteCommitment,
  type UpdateCommitmentData,
} from "@/hooks/useCommitments";
import { CommitmentForm, type CommitmentFormData } from "@/components/CommitmentForm";

export const Route = createFileRoute(
  "/projects/$projectId/commitments/$commitmentId" as any
)({
  component: CommitmentDetailPage,
});

function CommitmentDetailPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const commitmentId = "commitmentId" in params ? params.commitmentId : "";
  const navigate = useNavigate();
  const { data, isLoading } = useCommitment(projectId, commitmentId);
  const updateCommitment = useUpdateCommitment(projectId);
  const deleteCommitment = useDeleteCommitment(projectId);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  const commitment = data?.commitment;

  const handleEdit = async (formData: CommitmentFormData) => {
    try {
      const updateData: UpdateCommitmentData = {
        vendorName: formData.vendorName,
        amount: formData.amount,
        description: formData.description || undefined,
        status: formData.status,
      };

      await updateCommitment.mutateAsync({ id: commitmentId, data: updateData });

      notifications.show({
        title: "Success",
        message: "Commitment updated successfully!",
        color: "green",
      });

      closeEdit();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update commitment",
        color: "red",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCommitment.mutateAsync(commitmentId);

      notifications.show({
        title: "Success",
        message: "Commitment deleted successfully!",
        color: "green",
      });

      navigate({
        to: "/projects/$projectId/commitments",
        params: { projectId },
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete commitment",
        color: "red",
      });
    }
  };

  const handleBack = () => {
    navigate({
      to: "/projects/$projectId/commitments",
      params: { projectId },
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "gray";
      case "issued":
        return "blue";
      case "received":
        return "orange";
      case "paid":
        return "green";
      default:
        return "gray";
    }
  };

  const getTypeIcon = (type: string) => {
    return type === "po" ? <IconFileInvoice size={24} /> : <IconContract size={24} />;
  };

  const getTypeLabel = (type: string) => {
    return type === "po" ? "Purchase Order" : "Subcontract";
  };

  if (isLoading) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={400} radius="md" />
        </Stack>
      </Paper>
    );
  }

  if (!commitment) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="md" align="center">
          <Text size="lg" c="dimmed">
            Commitment not found
          </Text>
          <Button onClick={handleBack}>Back to Commitments</Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Button
              variant="subtle"
              leftSection={<IconChevronLeft size={16} />}
              onClick={handleBack}
            >
              Back to Commitments
            </Button>

            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon variant="subtle" size="lg">
                  <IconDotsVertical size={20} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item leftSection={<IconEdit size={16} />} onClick={openEdit}>
                  Edit Commitment
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={openDelete}
                >
                  Delete Commitment
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* Header with vendor name */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs" mb="xs">
                {getTypeIcon(commitment.type)}
                <Title order={2}>{commitment.vendorName}</Title>
              </Group>
              <Group gap="xs">
                <Badge variant="light" size="lg">
                  {getTypeLabel(commitment.type)}
                </Badge>
                <Badge color={getStatusColor(commitment.status)} variant="light" size="lg">
                  {commitment.status}
                </Badge>
              </Group>
            </div>
          </Group>

          {/* Amount - prominently displayed */}
          <Paper p="md" withBorder style={{ background: "var(--mantine-color-blue-0)" }}>
            <Group gap="xs" mb="xs">
              <IconCurrencyDollar size={20} stroke={1.5} />
              <Text fw={500} size="sm" c="dimmed">
                Commitment Amount
              </Text>
            </Group>
            <Text size="2rem" fw={700}>
              {formatCurrency(commitment.amount)}
            </Text>
          </Paper>

          {/* Cost Code Information */}
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Text fw={500} size="sm" c="dimmed">
                Cost Code
              </Text>
              <div>
                <Text fw={600}>{commitment.costCode}</Text>
                <Text size="sm" c="dimmed">
                  {commitment.costCodeName}
                </Text>
              </div>
            </Stack>
          </Paper>

          {/* Date */}
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Group gap="xs">
                <IconCalendar size={20} stroke={1.5} />
                <Text fw={500} size="sm" c="dimmed">
                  Date
                </Text>
              </Group>
              <Text>{formatDate(commitment.date)}</Text>
            </Stack>
          </Paper>

          {/* Description (if present) */}
          {commitment.description && (
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text fw={500} size="sm" c="dimmed">
                  Description
                </Text>
                <Text style={{ whiteSpace: "pre-wrap" }}>{commitment.description}</Text>
              </Stack>
            </Paper>
          )}

          {/* Metadata */}
          <Paper p="sm" withBorder style={{ background: "var(--mantine-color-gray-0)" }}>
            <Group gap="md">
              <Text size="xs" c="dimmed">
                Created: {new Date(commitment.createdAt).toLocaleString()}
              </Text>
            </Group>
          </Paper>
        </Stack>
      </Paper>

      {/* Edit Modal */}
      <Modal
        opened={editOpened}
        onClose={closeEdit}
        title="Edit Commitment"
        size="lg"
        centered
      >
        <CommitmentForm
          onSubmit={handleEdit}
          onCancel={closeEdit}
          initialData={commitment}
          loading={updateCommitment.isPending}
          submitText="Save Changes"
          projectId={projectId}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Delete Commitment"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this commitment? This action cannot be undone.
          </Text>
          <Paper p="sm" withBorder>
            <Text size="sm" fw={500}>
              {commitment.vendorName}
            </Text>
            <Text size="sm" c="dimmed">
              {formatCurrency(commitment.amount)} - {getTypeLabel(commitment.type)}
            </Text>
          </Paper>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={closeDelete}
              disabled={deleteCommitment.isPending}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteCommitment.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
